/* eslint-disable react-hooks/exhaustive-deps -- effets alignés chargement id */
import React, { useEffect, useReducer, useState } from 'react';
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';

import U2000DefaultForm from '../../u2000Core/components/formControls/U2000DefaultForm';

import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000GetHandlers } from '../../u2000Core/hooks/handler/U2000UseGetHandlers';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import type { U2000SearchPayload, U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000SaveHandlers } from '../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from '../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3157ROLES } from '../U3157';
import U3157WorkNatureImportModelBody from './form/u3157WorkNatureImportModel/U3157WorkNatureImportModelBody';
import { U3157WorkNatureImportModelFormToolBar } from './form/u3157WorkNatureImportModel/U3157WorkNatureImportModelFormToolBar';
import U3157WorkNatureImportModelDeleteDlg from './common/U3157WorkNatureImportModelDeleteDlg';
import { useU3157Context } from '../U3157Context';
import U2000AuditPanelComponent from '../../u2000Core/components/audit/U2000AuditPanelComponent';
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  createEmptyU3157FormRow,
  luHandleAppiError,
  normalizeU3157FromApi,
  u3157FormRowToSearchRow,
  type U3157ImportModel,
  type U3157ImportModelFormRow,
  type U3157ImportModelSearchRow,
} from '../U3157Service';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U2000Tab from '../../u2000Core/components/tab/U2000Tab';

function normalizeForForm(row: U3157ImportModel): U3157ImportModelFormRow {
  return normalizeU3157FromApi(row);
}

function validateU3157Form(
  row: U3157ImportModelFormRow,
  t: (key: string, opts?: Record<string, unknown>) => string,
  nameSpace: string,
  isFr: boolean,
): Record<string, string> {
  const result: Record<string, string> = {};
  const code = row.name?.trim() ?? '';
  if (!code) {
    result['name'] = t('errMandateTypeCodeRequired', {
      ns: nameSpace,
      defaultValue: isFr ? 'Le code type de mandat est obligatoire.' : 'Mandate type code is required.',
    });
  } else if (code.length > 255) {
    result['name'] = t('errMandateTypeCodeMax', {
      ns: nameSpace,
      defaultValue: isFr ? 'Le code ne peut pas dépasser 255 caractères.' : 'Code cannot exceed 255 characters.',
    });
  }
  if (!row.workNatureIds?.length) {
    result['workNatureIds'] = t('errWorkNatureIdsRequired', {
      ns: nameSpace,
      defaultValue: isFr ? 'Au moins une nature des travaux est requise.' : 'At least one work nature is required.',
    });
  }
  return result;
}

export interface U3157ImportModelFormExtendedHandlers extends U2000FormStateHandlersReturn<U3157ImportModelFormRow> {
  addNewImportModel: () => void;
  modifyImportModel: () => void;
  deleteImportModel: () => Promise<void>;
}

const U3157WorkNatureImportModelForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3157Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, unknown>(u2000FormState, u2000IntFormState);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith('fr');
  const language = i18n.resolvedLanguage ?? i18n.language;
  const listTitle = t('title', { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const u3157BL: U2000FormSBusinessHandlers<U3157ImportModelFormRow> = {
    newRecord: () => createEmptyU3157FormRow(),

    validateForm: (row: U3157ImportModelFormRow) => validateU3157Form(row, t, NS, isFr),

    saveForme: async (row: U3157ImportModelFormRow) => {
      const errors = validateU3157Form(row, t, NS, isFr);
      if (Object.keys(errors).length > 0) {
        u3157FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, u3157FormExtended.getFormEditMode());
    },

    cancelForm: () => {
      const old = u3157FormExtended.getOldData() as U3157ImportModelFormRow | null;
      if (old) {
        u3157FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === '/U3157') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    onLeaveForm: () => {
      navigate('/U3157');
    },
  };

  const u3157FormHandlers = useU2000FormStateHandlers<U3157ImportModelFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3157BL,
  });

  const u3157FormExtended: U3157ImportModelFormExtendedHandlers = {
    ...u3157FormHandlers,
    addNewImportModel: () => {
      u3157FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyImportModel: () => {
      u3157FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteImportModel: async () => {
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3157ImportModelSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3157ImportModelSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3157ImportModelFormRow>({
    doGetFn: (rowId: string) => {
      return doGetData(u2000AxiosAuthenticated, rowId);
    },
    onSuccess: (row: U3157ImportModelFormRow) => {
      const normalized = normalizeForForm(row as unknown as U3157ImportModel);
      const existing = gridStateHandlers.getData().find((r) => r.id === normalized.id);
      gridStateHandlers.setDataById(normalized.id, u3157FormRowToSearchRow(normalized, existing));
      u3157FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const crumbLabel = normalized.name?.trim() || normalized.id;
      setLogicalUnitBreadcrumb([{ href: '/U3157', label: listTitle }, { label: crumbLabel }]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3157FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3157ImportModelFormRow>({
    doSaveFn: (row: U3157ImportModelFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3157ImportModelFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeForForm(row as unknown as U3157ImportModel);
      const returnToListAfterSave =
        (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD ||
          luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) &&
        luStateHandlers.getActionOriginFrom() === '/U3157';

      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), u3157FormRowToSearchRow(normalized)]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        const existing = gridStateHandlers.getData().find((r) => r.id === row.id);
        gridStateHandlers.setDataById(row.id, u3157FormRowToSearchRow(normalized, existing));
      }

      u3157FormExtended.afterSaveData(normalized);
      toast.success(t('msgLogicalUnitSaved', { ns: NS }));

      if (returnToListAfterSave) {
        navigate('/U3157', { replace: true });
        return;
      }
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3157FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3157FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3157FormExtended.getData()],
    doDeleteFn: async (rowId, row) => {
      const r = row as U3157ImportModelFormRow;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(r));
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        const d = del as U3157ImportModelFormRow;
        toast.success(t('success0003', { key: d.name ?? d.id, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate('/U3157', { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === 'new') {
      u3157FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3157FormExtended.setData(createEmptyU3157FormRow());
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  if (u3157FormExtended.getData() === null) {
    return null;
  }

  const formTabs = [
    hasRole(U3157ROLES.AUDITS) && {
      id: 'tabAudits',
      label: t('tabAudits', { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={u3157FormExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U3157ImportModelAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3157WorkNatureImportModelDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3157WorkNatureImportModelFormToolBar formStateHandlers={u3157FormExtended} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3157FormExtended as never}
      >
        <U3157WorkNatureImportModelBody formStateHandlers={u3157FormExtended} t={t} language={language} />

        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={formTabs[0]?.id}
            activeTab={activeTab ?? formTabs[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === 'string' ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3157WorkNatureImportModelForm;
